"use client";

import { FirebaseError } from "firebase/app";
import { createUserWithEmailAndPassword, onAuthStateChanged, signInWithEmailAndPassword, signOut as firebaseSignOut, updateProfile, type User } from "firebase/auth";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { HangerIcon } from "@/components/mirror/BrandMark";
import { Button } from "@/components/ui/button";
import { alankarAuthUrl, getAlankarAuth } from "@/lib/firebase";

export type AlankarUser = {
  uid: string;
  email: string;
  name: string;
  initials: string;
};

type AuthContextValue = {
  user: AlankarUser | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (name: string, email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);
const SHARED_EMAIL = "admin@alankar.studio";
const SHARED_PASSWORD = "alankar123";

function initials(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "A";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
}

function mapUser(user: User): AlankarUser {
  const name = user.displayName || user.email || "Alankar";
  return {
    uid: user.uid,
    email: user.email || "",
    name,
    initials: initials(name),
  };
}

function authMessage(error: unknown) {
  const code = error instanceof FirebaseError ? error.code : "";
  if (code === "auth/invalid-credential" || code === "auth/wrong-password" || code === "auth/user-not-found") {
    return "That email or password does not match an Alankar ID.";
  }
  if (code === "auth/email-already-in-use") return "An Alankar ID already uses this email.";
  if (code === "auth/weak-password") return "Use at least 6 characters.";
  if (code === "auth/invalid-email") return "Enter a valid email.";
  if (code === "auth/network-request-failed") return "Sign-in could not reach the Alankar account service. Check the connection and try again.";
  return "Sign-in failed. Try again.";
}

async function recordLogin() {
  const token = await getAlankarAuth().currentUser?.getIdToken();
  if (!token) return;
  await fetch(`${alankarAuthUrl()}/alankar/login-event`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify({ app: "mirror" }),
  });
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AlankarUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(getAlankarAuth(), (next) => {
      setUser(next ? mapUser(next) : null);
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    await signInWithEmailAndPassword(getAlankarAuth(), email.trim(), password);
    await recordLogin();
  }, []);

  const signUp = useCallback(async (name: string, email: string, password: string) => {
    const credential = await createUserWithEmailAndPassword(getAlankarAuth(), email.trim(), password);
    await updateProfile(credential.user, { displayName: name.trim() });
    setUser(mapUser(credential.user));
    await recordLogin();
  }, []);

  const signOut = useCallback(async () => {
    await firebaseSignOut(getAlankarAuth());
  }, []);

  const value = useMemo(() => ({ user, loading, signIn, signUp, signOut }), [user, loading, signIn, signUp, signOut]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
}

export function AuthGate({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="grid h-dvh place-items-center bg-ivory text-muted">Checking your Alankar ID…</div>
    );
  }
  if (!user) return <LoginScreen />;
  return children;
}

function LoginScreen() {
  const { signIn, signUp } = useAuth();
  const [mode, setMode] = useState<"sign-in" | "create">("sign-in");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [pending, setPending] = useState(false);

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    setError("");
    setPending(true);
    try {
      if (mode === "create") {
        if (name.trim().length < 2) {
          setError("Enter the name for this Alankar ID.");
          return;
        }
        await signUp(name, email, password);
      } else {
        await signIn(email, password);
      }
    } catch (caught) {
      setError(authMessage(caught));
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="grid min-h-dvh place-items-center bg-ivory px-4 py-10">
      <div className="w-full max-w-md rounded-[28px] bg-paper p-8 shadow-lift">
        <div className="flex items-center gap-3">
          <div className="grid size-12 place-items-center rounded-full border border-line">
            <HangerIcon className="size-7" />
          </div>
          <div>
            <p className="font-serif text-2xl tracking-[0.16em]">ALANKAR</p>
            <p className="text-[11px] tracking-[0.28em] text-muted">MIRROR</p>
          </div>
        </div>
        <h1 className="mt-8 text-2xl font-semibold">Sign in with your Alankar ID</h1>
        <p className="mt-2 text-sm text-muted">The same email and password opens Alankar Studio and Alankar Mirror.</p>

        <form className="mt-6 flex flex-col gap-3" onSubmit={onSubmit}>
          {mode === "create" ? (
            <label className="text-sm font-medium">
              Name
              <input
                value={name}
                onChange={(event) => setName(event.target.value)}
                autoComplete="name"
                className="mt-1 h-12 w-full rounded-2xl border border-line bg-white px-4 text-base outline-none focus:border-pink"
              />
            </label>
          ) : null}
          <label className="text-sm font-medium">
            Email
            <input
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              autoComplete="username"
              required
              className="mt-1 h-12 w-full rounded-2xl border border-line bg-white px-4 text-base outline-none focus:border-pink"
            />
          </label>
          <label className="text-sm font-medium">
            Password
            <input
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete={mode === "create" ? "new-password" : "current-password"}
              minLength={6}
              required
              className="mt-1 h-12 w-full rounded-2xl border border-line bg-white px-4 text-base outline-none focus:border-pink"
            />
          </label>
          {error ? (
            <p role="alert" className="text-sm text-[#9f1239]">
              {error}
            </p>
          ) : null}
          <Button type="submit" variant="accent" size="lg" loading={pending} className="mt-2">
            {mode === "create" ? "Create Alankar ID" : "Sign in"}
          </Button>
        </form>

        <button
          type="button"
          className="mt-4 h-12 w-full rounded-full border border-line text-sm font-medium"
          onClick={() => {
            setMode("sign-in");
            setEmail(SHARED_EMAIL);
            setPassword(SHARED_PASSWORD);
            setError("");
          }}
        >
          Use the shared studio account
        </button>
        <button
          type="button"
          className="mt-3 h-12 w-full text-sm font-medium text-pink"
          onClick={() => {
            setMode((current) => (current === "sign-in" ? "create" : "sign-in"));
            setError("");
          }}
        >
          {mode === "sign-in" ? "Create a new Alankar ID" : "I already have an Alankar ID"}
        </button>
      </div>
    </div>
  );
}
